/**
 * Stepping through the fields a signer must fill.
 *
 * On a long contract the fields are needles in a haystack: a signer scrolls
 * looking for the next thing to touch, and the only feedback is a count that
 * tells them how many remain, not where. These helpers drive a pair of arrows
 * in the action bar that walk the document instead.
 *
 * Deliberately positional, not "jump to the next INCOMPLETE field". Skipping
 * what is already filled sounds more helpful and behaves worse: a signer who
 * wants to re-read what they put in the first box finds the control refuses to
 * go there. Position is predictable, and the count of what is left is already
 * stated beside it.
 */

export interface NavigableField {
  fieldId: string;
  page: number;
  /** Fractional position on the page, as stored. */
  x: number;
  y: number;
}

/**
 * Required fields in the order a reader meets them: file by file, page by
 * page, then down the page and across.
 *
 * `data.fields` arrives in whatever order the server returned, which is not
 * reading order — so arrows driven by it would appear to jump around. The `y`
 * comparison is bucketed to 1% of page height so two fields on the same line
 * order left-to-right rather than by a pixel of drift.
 */
export function orderRequiredFields<T extends NavigableField & { required?: boolean }>(
  fileIds: string[],
  fieldsByFile: Record<string, T[]>,
): Array<T & { fileId: string }> {
  const ordered: Array<T & { fileId: string }> = [];
  for (const fileId of fileIds) {
    const fields = (fieldsByFile[fileId] ?? []).filter((f) => f.required !== false);
    ordered.push(
      // The file travels with the field: the navigator needs it to address the
      // page container, and a page number alone is ambiguous across files.
      ...[...fields]
        .map((f) => ({ ...f, fileId }))
        .sort((a, b) => {
          if (a.page !== b.page) return a.page - b.page;
          const row = Math.round(a.y * 100) - Math.round(b.y * 100);
          if (row !== 0) return row;
          return a.x - b.x;
        }),
    );
  }
  return ordered;
}

/**
 * Next position, 1-based, wrapping at both ends.
 *
 * `0` means "not started" — the bar opens reading `0 of 3`, so the first press
 * of either arrow lands somewhere sensible rather than doing nothing. Wrapping
 * keeps both arrows live at every position: an arrow that greys out at the end
 * of a list is a dead end a signer has to notice and reason about.
 */
export function stepPosition(current: number, total: number, direction: 1 | -1): number {
  if (total <= 0) return 0;
  if (current <= 0) return direction === 1 ? 1 : total;
  const next = current + direction;
  if (next > total) return 1;
  if (next < 1) return total;
  return next;
}

/**
 * How many animation frames to wait for a page to paint before giving up.
 *
 * ~5 seconds at 60fps. The first cut allowed one second, which is fine for the
 * desktop case that motivated it and too short for the one that needs it most:
 * a phone, on a complex page, where `getPage` plus a full `page.render()` can
 * comfortably exceed a second. Giving up early is not harmless — the page has
 * been scrolled roughly into view, so it LOOKS like it worked while the field
 * is never centred, never focused, and never flashed.
 */
const REVEAL_MAX_FRAMES = 300;

export interface RevealDeps {
  doc?: Document;
  /** Injected for tests; defaults to `requestAnimationFrame`. */
  nextFrame?: (cb: () => void) => void;
  /** Injected for tests, which would otherwise burn the full budget. */
  maxFrames?: number;
}

/**
 * Scroll a field into view and put the caret in it.
 *
 * The awkward part is that the field may not exist yet. Pages paint lazily —
 * a page's overlay children only mount once its canvas has rendered — so on a
 * forty-page contract the field the signer asked for is usually not in the DOM
 * at the moment they ask. The page CONTAINER is, because it reserves its
 * height from the start, so scrolling that is what causes the paint; then the
 * field appears and can be centred properly.
 *
 * Focus, not just scroll: it is what makes the arrows work for a keyboard or
 * screen-reader user, and it leaves the next field ready to type into.
 */
export async function revealField(
  fieldId: string,
  pageKey: string | null,
  deps: RevealDeps = {},
): Promise<boolean> {
  const doc = deps.doc ?? document;
  const nextFrame =
    deps.nextFrame ??
    ((cb: () => void) => {
      if (typeof requestAnimationFrame === 'function') requestAnimationFrame(cb);
      else setTimeout(cb, 16);
    });

  const find = () => doc.querySelector<HTMLElement>(`[data-ds-field-id="${cssEscape(fieldId)}"]`);

  let el = find();
  if (!el && pageKey) {
    // Nudge the page into view so its IntersectionObserver fires and it paints.
    doc
      .querySelector<HTMLElement>(`[data-pdf-page-key="${cssEscape(pageKey)}"]`)
      ?.scrollIntoView?.({ behavior: 'auto', block: 'center' });
    el = await waitFor(find, nextFrame, deps.maxFrames ?? REVEAL_MAX_FRAMES);
  }
  if (!el) return false;

  el.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  // `preventScroll` because the smooth scroll above is already running —
  // letting focus scroll too fights it and lands the field somewhere else.
  focusTarget(el).focus?.({ preventScroll: true });
  return true;
}

/**
 * The thing that should actually take the caret.
 *
 * `data-ds-field-id` marks the positioned OVERLAY, which for a checkbox is a
 * `<label>` wrapping the input. A label is not focusable, so focusing it did
 * nothing at all — the scroll worked and keyboard and screen-reader users were
 * simply left where they were, which is the half of the promise that matters
 * most to them. Falling through to a nested control keeps the attribute where
 * the geometry is and fixes every wrapper of this shape, not just this one.
 */
function focusTarget(el: HTMLElement): HTMLElement {
  if (el.tabIndex >= 0 || /^(INPUT|TEXTAREA|SELECT|BUTTON|A)$/.test(el.tagName)) return el;
  return el.querySelector<HTMLElement>('input, textarea, select, button, [tabindex]') ?? el;
}

/** Poll across frames until the element turns up, or we run out of patience. */
function waitFor(
  find: () => HTMLElement | null,
  nextFrame: (cb: () => void) => void,
  maxFrames: number,
): Promise<HTMLElement | null> {
  return new Promise((resolve) => {
    let frames = 0;
    const tick = () => {
      const el = find();
      if (el) return resolve(el);
      if (++frames >= maxFrames) return resolve(null);
      nextFrame(tick);
    };
    nextFrame(tick);
  });
}

/**
 * Quote a value for an attribute selector.
 *
 * Ids here are server-generated and well-formed, so this is belt and braces —
 * but a selector built by concatenation is the kind of thing that stops being
 * safe the moment an id format changes, and the failure would be a silent
 * no-op arrow rather than an error.
 */
function cssEscape(value: string): string {
  return value.replace(/["\\]/g, '\\$&');
}

/** The minimum a fill has to carry for a field to count as done. */
export interface FieldFillLike {
  // Nullable as well as optional: the signing page's own `FieldFill` models a
  // cleared value as null, and a shape that refused it would force a cast at
  // the one call site that matters.
  value?: string | null;
  dataUrl?: string | null;
}

/**
 * Is this field satisfied?
 *
 * ONE definition, shared by the "N left to complete" count and by "Find next".
 * If the two ever disagreed the bar would contradict itself — the count
 * insisting something is outstanding while the button refuses to go anywhere,
 * or the reverse — and the signer would have no way to tell which was lying.
 *
 * A text field counts at one character, which is the platform's existing rule
 * and not something this changes.
 */
export function isFieldComplete(type: string, fill: FieldFillLike | undefined): boolean {
  if (type === 'signature' || type === 'initial') return !!fill?.dataUrl;
  // `=== 'true'`, NOT a truthiness test. Unticking a checkbox writes the STRING
  // 'false', which is truthy — so a required "I agree" box that was ticked and
  // then cleared counted as complete, Finish enabled, and Find next reported
  // nothing outstanding so it could not route the signer back to it. The
  // envelope could be submitted with a required consent box unchecked.
  if (type === 'checkbox') return fill?.value === 'true';
  return !!fill?.value && fill.value.length > 0;
}

/**
 * Position (1-based) of the FIRST field still needing action, or 0 if none do.
 *
 * Deliberately "first", not "next after where you are". That makes the control
 * stateless: it means one thing however many times it is pressed, and it
 * cannot leave an outstanding field behind the way a forward-only cursor does
 * until it has wrapped the whole way round. Filling a field removes it from
 * the set, so repeated presses walk the document top to bottom on their own.
 */
export function firstIncompletePosition<T extends { fieldId: string; type: string }>(
  fields: T[],
  fills: Record<string, FieldFillLike | undefined>,
): number {
  const index = fields.findIndex((f) => !isFieldComplete(f.type, fills[f.fieldId]));
  return index === -1 ? 0 : index + 1;
}
